import React, { useState, useEffect } from "react";
import { concat, keccak256, toUtf8Bytes, AbiCoder,ethers } from "ethers";
import governorAbi from "../abi/SimpleGovernor.json";
import catCoinAbi from "../abi/CatCoin.json";
import contestAbi from "../abi/CatContest.json";

const ProposalList = () => {
  // ========== STATES ==========
  const [proposals, setProposals] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, succeeded: 0, executed: 0 });
  const [filter, setFilter] = useState("All");
  const [sortOrder, setSortOrder] = useState("desc");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [account, setAccount] = useState(null);
  const [proposalType, setProposalType] = useState("ParameterChange");
  const [proposalDesc, setProposalDesc] = useState("");
  const [params, setParams] = useState({});
  const [defaults, setDefaults] = useState({}); // 🔧 auto-fill values

  const [page, setPage] = useState(1);
  const pageSize = 20;

  const governorAddr = process.env.REACT_APP_GOVERNOR_CONTRACT;

  // ========== UTILITIES ==========
  const mapState = (s) => {
    const states = [
      "Pending",      // Waiting for voting to start
      "Active",       // Currently voting
      "Defeated",     // Failed to pass
      "Succeeded",    // Passed, ready for timelock queue
      "Queued",       // In timelock queue
      "Executed",     // Successfully executed
      "Cancelled"     // Cancelled by proposer or admin
    ];
    return states[s] || "Unknown";
  };

  const fmt = (x, d = 18) => parseFloat(ethers.formatUnits(x, d));
  

  const toWei = (x) => {
    try {
      if (!x || isNaN(x)) return 0n;
      return ethers.parseUnits(String(x), 18);
    } catch {
      return 0n;
    }
  };

  // ========== LOAD ACCOUNT ==========
  const loadAccount = async () => {
    if (!window.ethereum) return;
    const provider = new ethers.BrowserProvider(window.ethereum);
    const accounts = await provider.listAccounts();
    if (accounts.length > 0) setAccount(accounts[0].address);
  };

  // ========== LOAD PROPOSALS ==========
  const loadProposals = async () => {
    try {
      setLoading(true);
      console.log("🔍 Loading proposals from MetaMask provider...");

      if (!window.ethereum) {
        alert("⚠️ Please install MetaMask first!");
        setLoading(false);
        return;
      }

      // ✅ 连接 MetaMask Provider
      const provider = new ethers.BrowserProvider(window.ethereum);
      const governor = new ethers.Contract(governorAddr, governorAbi, provider);

      // ✅ 检查 Governor 地址是否为有效合约
      const code = await provider.getCode(governorAddr);
      if (!code || code === "0x") {
        alert("⚠️ Governor contract not found. Please check your .env configuration.");
        setLoading(false);
        return;
      }

      // ✅ 获取提案总数
      let totalProposals = 0;
      try {
        totalProposals = Number(await governor.proposalCount());
        console.log(`📊 proposalCount(): ${totalProposals}`);
      } catch (err) {
        console.warn("⚠️ proposalCount() not found, falling back to sequential detection...");
        for (let i = 1; i <= 500; i++) {
          try {
            await governor.proposals(i);
            totalProposals = i;
          } catch {
            break;
          }
        }
      }

      if (totalProposals === 0) {
        console.log("ℹ️ No proposals on-chain.");
        setProposals([]);
        setStats({ total: 0, active: 0, succeeded: 0, executed: 0 });
        setLoading(false);
        return;
      }

      // ✅ 逐个加载提案
      const enriched = [];
      let active = 0, succeeded = 0, executed = 0, validCount = 0;

      for (let i = totalProposals; i >= 0; i--) {
        try {
          const proposal = await governor.proposals(i);
          const state = await governor.state(i);
          const votes = await governor.getProposalVotes(i);

          const label = mapState(state);
          if (label === "Active") active++;
          else if (["Succeeded", "Queued"].includes(label)) succeeded++;
          else if (label === "Executed") {succeeded++; executed++;}

          const cleanDesc = proposal.description || "(no description)";

          enriched.push({
            id: i,
            proposer: proposal.proposer || "N/A",
            description: cleanDesc,
            startBlock: Number(proposal.startBlock || 0),
            endBlock: Number(proposal.endBlock || 0),
            forVotes: parseFloat(ethers.formatUnits(votes.forVotes || 0n, 18)),
            againstVotes: parseFloat(ethers.formatUnits(votes.againstVotes || 0n, 18)),
            abstainVotes: parseFloat(ethers.formatUnits(votes.abstainVotes || 0n, 18)),
            state,
          });
          validCount++;
        } catch (err) {
          console.warn(`⚠️ Skipping invalid proposal #${i}:`, err.message);
          continue;
        }
      }

      // ✅ 更新状态
      setStats({
        total: validCount,
        active,
        succeeded,
        executed,
      });
      setProposals(enriched);
      console.log(`✅ Loaded ${validCount}/${totalProposals} proposals successfully`);
    } catch (err) {
      console.error("❌ loadProposals failed:", err);
      alert("⚠️ Unable to load proposals. Try reconnecting MetaMask or refreshing the page.");
    } finally {
      setLoading(false);
    }
  };









  // ========== LOAD DEFAULT PARAMS ==========
const loadDefaultParams = async () => {
  try {
    const provider = new ethers.BrowserProvider(window.ethereum);
    const token = new ethers.Contract(process.env.REACT_APP_TOKEN_CONTRACT, catCoinAbi, provider);
    const contest = new ethers.Contract(process.env.REACT_APP_CONTEST_CONTRACT, contestAbi, provider);

    const [mintInterval, mintAmount, winners, rewardPct, charityPct, votingPeriod, maxVotes] =
      await Promise.all([
        token.mintInterval({ blockTag: "latest" }),
        token.mintAmount({ blockTag: "latest" }),
        contest.winnersCount({ blockTag: "latest" }),
        contest.rewardPercentage({ blockTag: "latest" }),
        contest.charityPercentage({ blockTag: "latest" }),
        contest.votingPeriod({ blockTag: "latest" }),
        contest.maxVotesPerUser({ blockTag: "latest" }),
      ]);

    setDefaults({
      mintInterval: Number(mintInterval),
      mintAmount: fmt(mintAmount, 18).toFixed(2),
      winnersCount: Number(winners),
      rewardPct: Number(rewardPct),
      charityPct: Number(charityPct),
      votingPeriod: Number(votingPeriod) / 60, // sec → min
      maxVotes: parseFloat(ethers.formatUnits(maxVotes, 18)).toFixed(2),
    });
  } catch (err) {
    console.warn("⚠️ Failed to load DAO defaults:", err);
  }
};


  useEffect(() => {
    loadAccount();
    loadProposals();
    loadDefaultParams();
  }, []);

  // ========== CREATE PROPOSAL ==========
  // ✅ check eligibility
  const checkProposalEligibility = async () => {
    try {
      if (!window.ethereum) {
        alert("⚠️ Please install MetaMask first.");
        return false;
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const account = await signer.getAddress();

      const tokenAddr = process.env.REACT_APP_TOKEN_CONTRACT;
      const catToken = new ethers.Contract(tokenAddr, catCoinAbi, provider);
      const governor = new ethers.Contract(governorAddr, governorAbi, provider);

      const votes = await catToken.getVotes(account);
      const deposit = await governor.proposalDeposit();

      const votesNum = parseFloat(ethers.formatUnits(votes, 18));
      const thresholdNum = parseFloat(ethers.formatUnits(deposit, 18));

      console.log(`🗳️ Votes: ${votesNum}, Threshold: ${thresholdNum}`);

      if (votesNum < thresholdNum) {
        alert(
          `⚠️ You need at least ${thresholdNum} CAT voting power to create a proposal.\n` +
          `You currently have ${votesNum.toFixed(2)} CAT votes.\n\n` +
          `👉 To gain voting rights:\n` +
          `1️⃣ Go to the "🔌 Connect Your Wallet" section at the top.\n` +
          `2️⃣ Click the green 🗳 "Delegate Voting Power" button.\n` +
          `3️⃣ Confirm the transaction in MetaMask.\n\n` +
          `💡 After delegation, your CAT tokens will grant you voting power for DAO proposals.`
        );
        return false;
      }

      return true;
    } catch (err) {
      console.error("⚠️ Eligibility check failed:", err);
      alert(
        "⚠️ Unable to verify proposal eligibility.\n\n" +
        "Possible causes:\n" +
        "• MetaMask not connected\n" +
        "• RPC temporarily unavailable\n" +
        "• You may be on the wrong network\n\n" +
        "👉 Please reconnect MetaMask and try again."
      );
      return false;
    }
  };


  const buildCalldata = () => {
    switch (proposalType) {
      case "ParameterChange":
        return {
          target: process.env.REACT_APP_TOKEN_CONTRACT,
          data: ethers.AbiCoder.defaultAbiCoder().encode(
            ["uint256", "uint256"],
            [
              params.mintInterval || defaults.mintInterval || 600, 
              Number(params.mintAmount || defaults.mintAmount || 100)
            ]
          ),
          signature: "updateMintParameters(uint256,uint256)",
          desc: `Change CatCoin mintInterval → ${params.mintInterval || defaults.mintInterval}s, mintAmount → ${params.mintAmount || defaults.mintAmount} CAT`,
          typeCode: 0,
        };

      case "CharityDonation":
        return {
          target: process.env.REACT_APP_CHARITY_CONTRACT,
          data: ethers.AbiCoder.defaultAbiCoder().encode(
            ["address", "uint256", "string"],
            [params.charityAddr, ethers.parseEther(String(params.amount || 0)), params.note || "Governance donation"]
          ),
          signature: "createCharityProposal(address,uint256,string)",
          desc: `Donate ${params.amount} ETH to charity ${params.charityAddr}`,
          typeCode: 2,
        };

      case "ContestSettings":
        return {
          target: process.env.REACT_APP_CONTEST_CONTRACT,
          data: ethers.AbiCoder.defaultAbiCoder().encode(
            ["uint256", "uint256", "uint256", "uint256", "uint256"],
            [
              params.winnersCount || defaults.winnersCount || 3,
              params.rewardPct || defaults.rewardPct || 70,
              params.charityPct || defaults.charityPct || 20,
              (Number(params.votingPeriod || defaults.votingPeriod || 10) * 60),
              toWei(params.maxVotes || defaults.maxVotes || 1000),
            ]
          ),
          signature: "updateParameters(uint256,uint256,uint256,uint256,uint256)",
          desc: `Update contest: reward ${params.rewardPct || defaults.rewardPct}%, charity ${params.charityPct || defaults.charityPct}%, maxVotes ${params.maxVotes || defaults.maxVotes} CAT`,
          typeCode: 3,
        };

      case "TreasuryTransfer":
        return {
          target: process.env.REACT_APP_TREASURY_CONTRACT,
          data: ethers.AbiCoder.defaultAbiCoder().encode(["uint256"], [toWei(params.amount || 0)]),
          signature: "transferToCharityWallet(uint256)",
          desc: `Transfer ${params.amount} CAT from Treasury to CharityWallet`,
          typeCode: 4,
        };

      case "AddCharity":
        return {
          target: process.env.REACT_APP_CHARITY_CONTRACT,
          data: ethers.AbiCoder.defaultAbiCoder().encode(
            ["address", "string"],
            [params.charityAddr, params.charityName]
          ),
          signature: "addCharity(address,string)",
          desc: `Add new charity ${params.charityName} (${params.charityAddr}) to whitelist`,
          typeCode: 5,
        };

      default:
        return { target: "", data: "0x", signature: "", desc: "Unknown proposal", typeCode: 9 };
    }
  };

  // ========== CREATE PROPOSAL (HexConcat版) ==========
const createProposal = async () => {
  if (!proposalType) return alert("Please select proposal type");

  const eligible = await checkProposalEligibility();
  if (!eligible) return;

  try {
    setCreating(true);
    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    const governor = new ethers.Contract(governorAddr, governorAbi, signer);

    // --- Build calldata manually with hexConcat ---
    let target = "";
    let signature = "";
    let encodedParams = "0x";
    let desc = "";
    let typeCode = 0;

    switch (proposalType) {
      case "ParameterChange":
        target = process.env.REACT_APP_TOKEN_CONTRACT;
        signature = "updateMintParameters(uint256,uint256)";
        encodedParams = ethers.AbiCoder.defaultAbiCoder().encode(
          ["uint256", "uint256"],
          [
            params.mintInterval || defaults.mintInterval || 600,
            Number(params.mintAmount || defaults.mintAmount || 100),
          ]
        );
        desc = `Change CatCoin mintInterval → ${params.mintInterval || defaults.mintInterval}s, mintAmount → ${params.mintAmount || defaults.mintAmount} CAT`;
        typeCode = 0;
        break;

      case "CharityDonation":
        target = process.env.REACT_APP_CHARITY_CONTRACT;
        signature = "createCharityProposal(address,uint256,string)";
        encodedParams = ethers.AbiCoder.defaultAbiCoder().encode(
          ["address", "uint256", "string"],
          [
            params.charityAddr,
            ethers.parseEther(String(params.amount || 0)),
            params.note || "Governance donation",
          ]
        );
        desc = `Donate ${params.amount} ETH to charity ${params.charityAddr}`;
        typeCode = 2;
        break;

      case "ContestSettings":
        target = process.env.REACT_APP_CONTEST_CONTRACT;
        signature = "updateParameters(uint256,uint256,uint256,uint256,uint256)";
        encodedParams = ethers.AbiCoder.defaultAbiCoder().encode(
          ["uint256", "uint256", "uint256", "uint256", "uint256"],
          [
            params.winnersCount || defaults.winnersCount || 3,
            params.rewardPct || defaults.rewardPct || 70,
            params.charityPct || defaults.charityPct || 20,
            (Number(params.votingPeriod || defaults.votingPeriod || 10) * 60),
            toWei(params.maxVotes || defaults.maxVotes || 1000),
          ]
        );
        desc = `Update contest parameters: reward ${params.rewardPct || defaults.rewardPct}%, charity ${params.charityPct || defaults.charityPct}%, maxVotes ${params.maxVotes || defaults.maxVotes} CAT`;
        typeCode = 3;
        break;

      case "TreasuryTransfer":
        target = process.env.REACT_APP_TREASURY_CONTRACT;
        signature = "transferToCharityWallet(uint256)";
        encodedParams = ethers.AbiCoder.defaultAbiCoder().encode(
          ["uint256"],
          [toWei(params.amount || 0)]
        );
        desc = `Transfer ${params.amount} CAT from Treasury to CharityWallet`;
        typeCode = 4;
        break;

      default:
        alert("Unknown proposal type");
        setCreating(false);
        return;
    }

    // --- ✅ Compute selector and hexConcat ---
    const selector = keccak256(toUtf8Bytes(signature)).slice(0, 10);
    const calldata = concat([selector, encodedParams]);

    // --- ✅ Prepare proposal ---
    const targets = [target];
    const values = [0];
    const calldatas = [calldata];
    const description = proposalDesc ? `${desc} — ${proposalDesc}` : desc;

    console.log("📤 Submitting proposal:");
    console.log("Target:", target);
    console.log("Signature:", signature);
    console.log("Selector:", selector);
    console.log("Calldata:", calldata);

    const tx = await governor.propose(targets, values, calldatas, description, typeCode);
    alert("📤 Creating proposal...");
    await tx.wait();

    alert("✅ Proposal created successfully!");
    setParams({});
    setProposalDesc("");
    setCreating(false);
    loadProposals();
  } catch (err) {
    console.error("❌ Proposal creation failed:", err);
    const reason = err?.error?.reason || err?.reason || err?.message || "Unknown error";

    if (reason.includes("Votes below threshold")) {
      alert(
        "⚠️ Proposal failed: your voting power is below the required threshold.\n" +
          "Please claim or delegate CAT tokens first."
      );
    } else {
      alert(`❌ Proposal creation failed.\n\nReason: ${reason}`);
    }

    setCreating(false);
  }
};



  const getInputStyle = (field) => ({
    width: "calc(100% - 20px)",
    margin: "0 10px 10px 10px",
    padding: "8px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    background: "#fff",
    color: params[field] ? "#000" : "#666",
    fontFamily: "monospace",
    boxSizing: "border-box",
  });

  // ========== VOTE ==========
  // ✅ Cast a vote with support type (0=Against, 1=For, 2=Abstain)
  const castVote = async (proposalId, support) => {
    if (!window.ethereum) return alert("⚠️ Please connect your wallet first.");
    try {
      setLoading(true);
      const provider = new ethers.BrowserProvider(window.ethereum);
      const signer = await provider.getSigner();
      const governor = new ethers.Contract(governorAddr, governorAbi, signer);

      const supportLabel =
        support === 1 ? "✅ For" : support === 0 ? "❌ Against" : "⚪ Abstain";
      alert(`🗳 Casting vote: ${supportLabel} ...`);

      const tx = await governor.castVote(proposalId, support);
      await tx.wait();

      alert(`🎉 Vote submitted successfully!`);
      loadProposals();
    } catch (err) {
      console.error("❌ Voting failed:", err);
      alert(
        err.reason
          ? `⚠️ ${err.reason}`
          : "⚠️ Voting transaction failed. Maybe proposal is not active or already executed."
      );
    } finally {
      setLoading(false);
    }
  };

  // ========== QUEUE PROPOSAL ==========
const queueProposal = async (proposalId) => {
  if (!window.ethereum) return alert("⚠️ Please connect wallet first.");
  try {
    setLoading(true);
    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    const governor = new ethers.Contract(governorAddr, governorAbi, signer);

    alert("📦 Queuing proposal into Timelock...");
    const tx = await governor.queue(proposalId);
    await tx.wait();

    alert("✅ Proposal queued successfully!");
    loadProposals();
  } catch (err) {
    console.error("❌ Queue failed:", err);
    alert(`⚠️ Failed to queue proposal.\n\n${err.reason || err.message}`);
  } finally {
    setLoading(false);
  }
};

// ========== EXECUTE PROPOSAL ==========
const executeProposal = async (proposalId) => {
  if (!window.ethereum) return alert("⚠️ Please connect wallet first.");
  try {
    setLoading(true);
    const provider = new ethers.BrowserProvider(window.ethereum);
    const signer = await provider.getSigner();
    const governor = new ethers.Contract(governorAddr, governorAbi, signer);

    alert("🚀 Executing proposal...");
    const tx = await governor.execute(proposalId);
    await tx.wait();

    alert("🎉 Proposal executed successfully!");
    loadProposals();
  } catch (err) {
    console.error("❌ Execute failed:", err);
    alert(`⚠️ Failed to execute proposal.\n\n${err.reason || err.message}`);
  } finally {
    setLoading(false);
  }
};


  // ========== FILTER ==========
  const filteredProposals = proposals
    .filter((p) => {
      if (filter === "All") return true;
      const st = mapState(p.state);
      if (filter === "Active") return st === "Active";
      if (filter === "Succeeded")
        return ["Succeeded", "Queued", "Executed"].includes(st);
      if (filter === "Executed") return st === "Executed";
      return false;
    })
    .sort((a, b) => (sortOrder === "desc" ? b.startBlock - a.startBlock : a.startBlock - b.startBlock));

  // ========== INPUT RENDER ==========
  const renderParamInputs = () => {
    switch (proposalType) {
      case "ParameterChange":
        return (
          <>
            <input
              type="number"
              value={params.mintInterval ?? defaults.mintInterval ?? ""}
              onChange={(e) => setParams({ ...params, mintInterval: e.target.value })}
              style={getInputStyle("mintInterval")}
            />
            <input
              type="number"
              value={params.mintAmount ?? defaults.mintAmount ?? ""}
              onChange={(e) => setParams({ ...params, mintAmount: e.target.value })}
              style={getInputStyle("mintAmount")}
            />
          </>
        );

      case "ContestSettings":
        return (
          <>
            <input
              type="number"
              value={params.winnersCount ?? defaults.winnersCount ?? ""}
              onChange={(e) => setParams({ ...params, winnersCount: e.target.value })}
              style={getInputStyle("winnersCount")}
            />
            <input
              type="number"
              value={params.rewardPct ?? defaults.rewardPct ?? ""}
              onChange={(e) => setParams({ ...params, rewardPct: e.target.value })}
              style={getInputStyle("rewardPct")}
            />
            <input
              type="number"
              value={params.charityPct ?? defaults.charityPct ?? ""}
              onChange={(e) => setParams({ ...params, charityPct: e.target.value })}
              style={getInputStyle("charityPct")}
            />
            <input
              type="number"
              value={params.votingPeriod ?? defaults.votingPeriod ?? ""}
              onChange={(e) => setParams({ ...params, votingPeriod: e.target.value })}
              style={getInputStyle("votingPeriod")}
            />
            <input
              type="number"
              value={params.maxVotes ?? defaults.maxVotes ?? ""}
              onChange={(e) => setParams({ ...params, maxVotes: e.target.value })}
              style={getInputStyle("maxVotes")}
            />
          </>
        );

      case "CharityDonation":
        return (
          <>
            <input
              placeholder="Charity Address"
              value={params.charityAddr || ""}
              onChange={(e) => setParams({ ...params, charityAddr: e.target.value })}
              style={getInputStyle("charityAddr")}
            />
            <input
              placeholder="Amount (ETH)"
              value={params.amount || ""}
              onChange={(e) => setParams({ ...params, amount: e.target.value })}
              style={getInputStyle("amount")}
            />
            <input
              placeholder="Optional note"
              value={params.note || ""}
              onChange={(e) => setParams({ ...params, note: e.target.value })}
              style={getInputStyle("note")}
            />
          </>
        );

      case "TreasuryTransfer":
        return (
          <input
            placeholder="Amount to transfer (CAT)"
            value={params.amount || ""}
            onChange={(e) => setParams({ ...params, amount: e.target.value })}
            style={getInputStyle("amount")}
          />
        );
      default:
        return null;
    }
  };

  const statCard = (label, value, color) => (
    <div
      onClick={() => setFilter(label)}
      style={{
        ...statBox,
        border: filter === label ? `2px solid ${color}` : "1px solid #ddd",
        cursor: "pointer",
      }}
    >
      <h3 style={{ color, margin: "0 0 6px 0" }}>{value}</h3>
      <p style={{ margin: 0, color: "#555", fontWeight: "bold" }}>{label}</p>
    </div>
  );

  // ========== RENDER ==========
  return (
    <div>
      {/* --- Create --- */}
      <section style={createBox}>
        <h3>📝 Create New Proposal</h3>

        <div style={formGrid}>
          {/* Proposal Type */}
          <div style={labelCol}>Proposal Type:</div>
          <div style={inputCol}>
            <select
              value={proposalType}
              onChange={(e) => setProposalType(e.target.value)}
              style={inputStyleGray}
            >
              <option value="ParameterChange">CatCoin Parameter Change</option>
              <option value="ContestSettings">Contest Settings</option>
              <option value="TreasuryTransfer">Treasury → CharityWallet Transfer (CAT)</option>
              <option value="AddCharity">Add Charity to Whitelist</option>
              <option value="CharityDonation">Charity Donation (ETH)</option>
            </select>
          </div>

          {/* CatCoin Parameters */}
          {proposalType === "ParameterChange" && (
            <>
              <div style={labelCol}>Mint Interval (sec):</div>
              <div style={inputCol}>
                <input
                  type="number"
                  value={params.mintInterval ?? defaults.mintInterval ?? ""}
                  onChange={(e) => setParams({ ...params, mintInterval: e.target.value })}
                  style={{
                    ...inputStyleWhite,
                    color: params.mintInterval ? "#000" : "#777",
                  }}
                />
              </div>

              <div style={labelCol}>Mint Amount (CAT):</div>
              <div style={inputCol}>
                <input
                  type="number"
                  value={params.mintAmount ?? defaults.mintAmount ?? ""}
                  onChange={(e) => setParams({ ...params, mintAmount: e.target.value })}
                  style={{
                    ...inputStyleWhite,
                    color: params.mintAmount ? "#000" : "#777",
                  }}
                />
              </div>
            </>
          )}


          {/* Charity Donation */}
          {proposalType === "CharityDonation" && (
            <>
              <div style={labelCol}>Charity Address:</div>
              <div style={inputCol}>
                <input
                  placeholder="0x... wallet"
                  value={params.charityAddr || ""}
                  onChange={(e) => setParams({ ...params, charityAddr: e.target.value })}
                  style={inputStyleWhite}
                />
              </div>

              <div style={labelCol}>Donation Amount (ETH):</div>
              <div style={inputCol}>
                <input
                  type="number"
                  placeholder="0.05"
                  value={params.amount || ""}
                  onChange={(e) => setParams({ ...params, amount: e.target.value })}
                  style={inputStyleWhite}
                />
              </div>
            </>
          )}



          {/* Contest Settings */}
          {proposalType === "ContestSettings" && (
            <>
              <div style={labelCol}>Winners Count:</div>
              <div style={inputCol}>
                <input
                  type="number"
                  placeholder="3"
                  value={params.winnersCount ?? defaults.winnersCount ?? ""}
                  onChange={(e) => setParams({ ...params, winnersCount: e.target.value })}
                  style={inputStyleWhite}
                />
              </div>

              <div style={labelCol}>Reward %:</div>
              <div style={inputCol}>
                <input
                  type="number"
                  placeholder="70"
                  value={params.rewardPct ?? defaults.rewardPct ?? ""}
                  onChange={(e) => setParams({ ...params, rewardPct: e.target.value })}
                  style={inputStyleWhite}
                />
              </div>

              <div style={labelCol}>Charity %:</div>
              <div style={inputCol}>
                <input
                  type="number"
                  placeholder="20"
                  value={params.charityPct ?? defaults.charityPct ?? ""}
                  onChange={(e) => setParams({ ...params, charityPct: e.target.value })}
                  style={inputStyleWhite}
                />
              </div>

              <div style={labelCol}>Voting Period (minutes):</div>
              <div style={inputCol}>
                <input
                  type="number"
                  placeholder="24"
                  value={params.votingPeriod ?? defaults.votingPeriod ?? ""}
                  onChange={(e) => setParams({ ...params, votingPeriod: e.target.value })}
                  style={inputStyleWhite}
                />
              </div>

              <div style={labelCol}>Max Votes (CAT):</div>
              <div style={inputCol}>
                <input
                  type="number"
                  placeholder="1000"
                  value={params.maxVotes ?? defaults.maxVotes ?? ""}
                  onChange={(e) => setParams({ ...params, maxVotes: e.target.value })}
                  style={inputStyleWhite}
                />
              </div>
            </>
          )}


          {/* Treasury → CharityWallet Transfer */}
          {proposalType === "TreasuryTransfer" && (
            <>
              <div style={labelCol}>Transfer Amount (CAT):</div>
              <div style={inputCol}>
                <input
                  type="number"
                  placeholder="1000"
                  value={params.amount || ""}
                  onChange={(e) => setParams({ ...params, amount: e.target.value })}
                  style={inputStyleWhite}
                />
              </div>
            </>
          )}

          {/* Charity Whitelist */}
          {proposalType === "AddCharity" && (
            <>
              <div style={labelCol}>Charity Address:</div>
              <div style={inputCol}>
                <input
                  placeholder="0x..."
                  value={params.charityAddr || ""}
                  onChange={(e) => setParams({ ...params, charityAddr: e.target.value })}
                  style={inputStyleWhite}
                />
              </div>

              <div style={labelCol}>Charity Name:</div>
              <div style={inputCol}>
                <input
                  placeholder="e.g., UNICEF"
                  value={params.charityName || ""}
                  onChange={(e) => setParams({ ...params, charityName: e.target.value })}
                  style={inputStyleWhite}
                />
              </div>
            </>
          )}

          {/* Notes */}
          <div style={labelCol}>Notes:</div>
          <div style={inputCol}>
            <textarea
              placeholder="Optional note or comment (will appear in description)"
              value={proposalDesc}
              onChange={(e) => setProposalDesc(e.target.value)}
              style={textAreaStyle}
            />
          </div>
        </div>

        <div style={{ marginTop: "1.5rem", textAlign: "left" }}>
          <button onClick={createProposal} style={btnCreate} disabled={creating}>
            {creating ? "Submitting..." : "🚀 Submit Proposal"}
          </button>
        </div>
      </section>


      {/* --- Summary --- */}
      <section style={summarySection}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3>📊 Proposal Summary</h3>
          <button onClick={loadProposals} style={btnRefresh}>🔄 Refresh</button>
        </div>

        <div style={summaryGrid}>
          {statCard("All", stats.total, "#999")}
          {statCard("Active", stats.active, "#ffb700")}
          {statCard("Succeeded", stats.succeeded, "#28a745")}
          {statCard("Executed", stats.executed, "#007bff")}
        </div>
        <p style={{ color: "#777", marginTop: "10px" }}>
          Click a category above to filter ↓
        </p>
      </section>

      {/* --- List --- */}
      <section style={cardStyle}>
        {/* --- Pagination --- */}
        <div style={filterBar}>
          <button
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            disabled={page === 1}
            style={btnPage}
          >
            ◀ Prev
          </button>
          <span style={{ margin: "0 12px" }}>
            Page {page} / {Math.ceil(proposals.length / pageSize) || 1}
          </span>
          <button
            onClick={() =>
              setPage((p) => Math.min(p + 1, Math.ceil(proposals.length / pageSize)))
            }
            disabled={page >= Math.ceil(proposals.length / pageSize)}
            style={btnPage}
          >
            Next ▶
          </button>
        </div>

        {loading ? (
          <p>Loading proposals...</p>
        ) : filteredProposals.length === 0 ? (
          <p>No proposals found for this category.</p>
        ) : (
          filteredProposals
            .slice((page - 1) * pageSize, page * pageSize)
            .map((p, i) => {
              let mainDesc = p.description;
              let note = "";
              if (p.description && p.description.includes("—")) {
                const parts = p.description.split("—");
                mainDesc = parts[0].trim();
                note = parts.slice(1).join("—").trim();
              }

              return (
                <div key={i} style={proposalCard}>
                  <b>
                    #{String(p.id)} — <span>{mapState(p.state)}</span>
                  </b>

                  <p style={{ margin: "6px 0", color: "#111", fontWeight: "500" }}>
                    {mainDesc}
                  </p>

                  {note && (
                    <div
                      style={{
                        backgroundColor: "#f7f7f7",
                        padding: "8px 10px",
                        borderRadius: "8px",
                        margin: "6px 0 10px",
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          color: "#444",
                          fontStyle: "italic",
                          fontSize: "14px",
                        }}
                      >
                        🗒️ {note}
                      </p>
                    </div>
                  )}

                  <p style={{ color: "#333", marginTop: "6px" }}>
                    🗳 Votes — ✅ {p.forVotes} | ❌ {p.againstVotes} | ⚪ {p.abstainVotes}
                  </p>

                  {mapState(p.state) === "Active" && (
                    <div style={{ marginTop: "0.5rem" }}>
                      <button onClick={() => castVote(p.id, 1)} style={btnYes}>
                        👍 Vote For
                      </button>
                      <button onClick={() => castVote(p.id, 0)} style={btnNo}>
                        👎 Vote Against
                      </button>
                      <button
                        onClick={() => castVote(p.id, 2)}
                        style={{
                          ...btnYes,
                          background: "#888",
                          marginLeft: "6px",
                        }}
                      >
                        ⚪ Abstain
                      </button>
                    </div>
                  )}
{mapState(p.state) === "Succeeded" && (
  <div style={{ marginTop: "0.5rem" }}>
    <button onClick={() => queueProposal(p.id)} style={{ ...btnYes, background: "#ffc107", color: "#000" }}>
      📦 Queue for Execution
    </button>
  </div>
)}

{mapState(p.state) === "Queued" && (
  <div style={{ marginTop: "0.5rem" }}>
    <button onClick={() => executeProposal(p.id)} style={{ ...btnYes, background: "#007bff" }}>
      🚀 Execute Proposal
    </button>
  </div>
)}

                </div>
              );
            })
        )}

      </section>
    </div>
  );
};

// ===== STYLE =====
const cardStyle = { padding: "1.5rem", borderRadius: "12px", background: "#f9f9f9" };
const createBox = { background: "#fff8e6", border: "1px solid #ffd966", borderRadius: "12px", padding: "1.5rem", marginBottom: "1.5rem" };
const inputStyleGray = { width: "calc(100% - 20px)", margin: "0 10px 10px 10px", padding: "8px", borderRadius: "6px", border: "1px solid #ccc", background: "#fff", color: "#000", fontFamily: "monospace", boxSizing: "border-box" };
const textAreaStyle = { width: "calc(100% - 20px)", margin: "0 10px 10px 10px", padding: "8px", height: "80px", borderRadius: "6px", border: "1px solid #ccc", resize: "none", background: "#fff", boxSizing: "border-box", };
const summarySection = { background: "#fff", borderRadius: "12px", padding: "1.5rem", marginBottom: "1.5rem", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" };
const summaryGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "1rem", marginTop: "1rem" };
const statBox = { background: "#fff", borderRadius: "10px", padding: "1rem", textAlign: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.1)", transition: "0.2s" };
const proposalCard = { background: "#fff", padding: "1rem", borderRadius: "8px", boxShadow: "0 1px 4px rgba(0,0,0,0.1)", marginBottom: "1rem" };
const filterBar = { display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: "1rem" };
const btnCreate = { background: "#ffcc33", border: "none", borderRadius: "6px", padding: "0.6rem 1rem", cursor: "pointer", fontWeight: "bold" };
const btnYes = { padding: "4px 10px", background: "#28a745", border: "none", color: "#fff", borderRadius: "6px", cursor: "pointer", marginRight: "6px" };
const btnNo = { ...btnYes, background: "#dc3545" };
const btnSort = { padding: "4px 10px", borderRadius: "6px", border: "1px solid #ddd", background: "#fff", cursor: "pointer" };
const btnRefresh = { background: "#ffcc33", border: "none", borderRadius: "8px", padding: "0.4rem 0.8rem", cursor: "pointer", fontWeight: "bold", boxShadow: "0 1px 4px rgba(0,0,0,0.15)" };
const btnPage = {
  padding: "6px 12px",
  borderRadius: "6px",
  border: "1px solid #ccc",
  background: "#fff",
  cursor: "pointer",
  fontWeight: "bold",
  minWidth: "80px",
};
const formGrid = {
  display: "grid",
  gridTemplateColumns: "220px 1fr",
  alignItems: "center",
  rowGap: "10px",
  columnGap: "14px",
  marginTop: "0.5rem",
  maxWidth: "700px",
};

const labelCol = {
  fontWeight: "600",
  color: "#333",
  textAlign: "right",
  paddingRight: "8px",
};

const inputCol = {
  display: "flex",
  alignItems: "center",
  width: "100%",
};

const inputStyleWhite = {
  width: "calc(100% - 20px)",
  margin: "0 10px 10px 10px", 
  padding: "8px", 
  borderRadius: "6px",
  border: "1px solid #ccc",
  backgroundColor: "#fff",
  color: "#000",
  fontSize: "0.95rem",
  boxSizing: "border-box",
};



export default ProposalList;
